import { useState, useEffect, useCallback } from 'react';
import axios from '../api';
import CreditoAutomovil from './CreditoAutomovil';
import { NominaPage } from './Gastos';
import ThemeToggle from '../components/ThemeToggle';

const MESES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];

const DEFAULT_ADMIN_PANEL_ITEMS = [
  { id: 'normal', icon: 'AD', label: 'Software completo', desc: 'Entrar al sistema administrativo', group: 'Principal', visible: true, order: 10 },
  { id: 'dashboard', icon: 'DB', label: 'Dashboard', desc: 'Indicadores generales', group: 'Principal', visible: true, order: 20 },
  { id: 'registros', icon: 'RG', label: 'Registros', desc: 'Ingresos, servicios y cartera', group: 'Principal', visible: true, order: 30 },
  { id: 'clientes', icon: 'CL', label: 'Clientes', desc: 'Fichas, cotizaciones e inventarios', group: 'Principal', visible: true, order: 40 },
  { id: 'gastos', icon: 'GT', label: 'Gastos', desc: 'Fijos y variables', group: 'Principal', visible: true, order: 50 },
  { id: 'iva', icon: 'IV', label: 'IVA', desc: 'IVA generado y descontable', group: 'Principal', visible: true, order: 60 },
  { id: 'rentabilidad', icon: 'RN', label: 'Rentabilidad', desc: 'Clientes, servicios y márgenes', group: 'Principal', visible: true, order: 70 },
  { id: 'cartera', icon: 'CT', label: 'Cartera', desc: 'Por cobrar y días de vencimiento', group: 'Principal', visible: true, order: 75 },
  { id: 'tecnicoEmpresas', icon: 'EM', label: 'Empresas', desc: 'Empresas seleccionadas e inventario', group: 'Empresas', visible: true, order: 80 },
  { id: 'tecnico', icon: 'TC', label: 'Técnico', desc: 'Registro, empresas y agenda', group: 'Empresas', visible: true, order: 90 },
  { id: 'personal', icon: 'PE', label: 'Personal', desc: 'Inventario y actas de entrega', group: 'Personal', visible: true, order: 95 },
  { id: 'ss', icon: 'SS', label: 'Seguridad Social', desc: 'Liquidación y pagos PILA', group: 'Personal', visible: true, order: 100 },
  { id: 'nomina', icon: 'NO', label: 'Nómina', desc: 'Liquidación, comprobantes e historial', group: 'Personal', visible: true, order: 110 },
  { id: 'credito', icon: 'CV', label: 'Crédito vehículo', desc: 'Cuotas, abonos y saldo', group: 'Personal', visible: true, order: 120 },
  { id: 'inversiones', icon: 'IN', label: 'Inversiones', desc: 'Trading, CDT y Bitcoin', group: 'Inversiones', visible: true, order: 130 },
  { id: 'academia', icon: 'AC', label: 'Academia', desc: 'Alumnos y pagos', group: 'Otros', visible: true, order: 150 },
  { id: 'configuracion', icon: 'CF', label: 'Configuración', desc: 'Usuarios, permisos y respaldos', group: 'Otros', visible: true, order: 160 },
];

const ADMIN_LANDING_GROUPS = [
  { key: 'dashboard', code: 'DB', label: 'Dashboard', ids: ['dashboard'] },
  { key: 'clientes', code: 'CL', label: 'Clientes y finanzas', ids: ['clientes', 'registros', 'iva', 'gastos', 'rentabilidad', 'cartera'] },
  { key: 'empresas', code: 'EM', label: 'Empresas y nómina', ids: ['tecnicoEmpresas', 'nomina', 'ss'] },
  { key: 'personal', code: 'IN', label: 'Inversiones y academia', ids: ['credito', 'academia', 'inversiones'] },
  { key: 'administracion', code: 'AD', label: 'Administración', ids: ['configuracion'] },
];

function completarPanelItems(items) {
  const recibidos = Array.isArray(items) ? items : [];
  const porId = new Map(recibidos.map(item => [item.id, item]));
  const baseIds = new Set(DEFAULT_ADMIN_PANEL_ITEMS.map(item => item.id));
  return [
    ...DEFAULT_ADMIN_PANEL_ITEMS.map(base => ({ ...base, ...(porId.get(base.id) || {}) })),
    ...recibidos.filter(item => !baseIds.has(item.id)),
  ];
}

const pesos = n => '$' + Math.round(n || 0).toLocaleString('es-CO');
const fmt   = (n, d=0) => (n || 0).toLocaleString('es-CO', { minimumFractionDigits: d });

function calcNomina(sal, aux) {
  const r = v => Math.round(v);
  const ibc            = sal - aux;
  const ibc_quincena   = r(ibc / 2);
  const quincena_base  = r(sal / 2);
  const salud_emp      = r(ibc_quincena * 0.04);
  const pension_emp    = r(ibc_quincena * 0.04);
  const total_ded      = salud_emp + pension_emp;
  const neto_quincena  = quincena_base - total_ded;
  const neto_mensual   = r(neto_quincena * 2);
  const devengado      = r(sal + aux);
  const salud_er       = r(ibc * 0.085);
  const pension_er     = r(ibc * 0.12);
  const arl            = r(ibc * 0.00522);
  const caja           = r(ibc * 0.04);
  const sena           = r(ibc * 0.02);
  const icbf           = r(ibc * 0.03);
  const total_pf       = salud_er + pension_er + arl + caja + sena + icbf;
  const costo_total    = r(sal + aux + total_pf);
  return {
    ibc, ibc_quincena, quincena_base, devengado,
    salud_emp, pension_emp, total_ded, neto_quincena, neto_mensual,
    salud_er, pension_er, arl, caja, sena, icbf, total_pf, costo_total,
    salud_total: salud_emp + salud_er,
    pension_total: pension_emp + pension_er,
    total_pila: salud_emp + salud_er + pension_emp + pension_er + arl + caja + sena + icbf,
  };
}


// ─── LANDING ────────────────────────────────────────────────────────────────
function Landing({ user, onLogout, onGo, theme, onToggleTheme }) {
  const hoy = new Date();
  const mesActual = MESES[hoy.getMonth()];
  const isAdmin = user?.rol === 'admin';
  const [panelItems, setPanelItems] = useState(DEFAULT_ADMIN_PANEL_ITEMS);
  const [grupoActivo, setGrupoActivo] = useState(null);
  // Preferencias personales: se superponen a la configuracion por rol.
  const [prefs, setPrefs] = useState({ favoritos: [], ocultos: [], orden: [] });
  const [personalizar, setPersonalizar] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    axios.get('/configuracion/panel-principal')
      .then(r => setPanelItems(completarPanelItems(r.data?.items)))
      .catch(() => setPanelItems(completarPanelItems(DEFAULT_ADMIN_PANEL_ITEMS)));
  }, [isAdmin]);
  useEffect(() => {
    if (!grupoActivo) return undefined;
    const cerrarConEscape = event => {
      if (event.key === 'Escape') setGrupoActivo(null);
    };
    document.addEventListener('keydown', cerrarConEscape);
    return () => document.removeEventListener('keydown', cerrarConEscape);
  }, [grupoActivo]);
  useEffect(() => {
    axios.get('/panel/preferencias')
      .then(r => setPrefs({
        favoritos: Array.isArray(r.data?.favoritos) ? r.data.favoritos : [],
        ocultos: Array.isArray(r.data?.ocultos) ? r.data.ocultos : [],
        orden: Array.isArray(r.data?.orden) ? r.data.orden : [],
      }))
      .catch(() => {});
  }, []);
  const guardarPrefs = next => {
    setPrefs(next);
    axios.put('/panel/preferencias', next).catch(() => {});
  };
  const toggleFavorito = id => guardarPrefs({
    ...prefs,
    favoritos: prefs.favoritos.includes(id) ? prefs.favoritos.filter(x => x !== id) : [...prefs.favoritos, id],
  });
  const toggleOculto = id => guardarPrefs({
    ...prefs,
    ocultos: prefs.ocultos.includes(id) ? prefs.ocultos.filter(x => x !== id) : [...prefs.ocultos, id],
    favoritos: prefs.ocultos.includes(id) ? prefs.favoritos : prefs.favoritos.filter(x => x !== id),
  });
  const saraCards = [
    { key: 'normal',   icon: 'AD', label: 'Modo Administrador', desc: 'Acceso completo al sistema' },
    { key: 'ss',       icon: 'SS', label: 'Seguridad Social',    desc: 'Liquidación y pagos PILA' },
    { key: 'nomina',   icon: 'NO', label: 'Nómina',              desc: 'Comprobantes y historial' },
    { key: 'credito',  icon: 'CV', label: 'Crédito vehículo',    desc: 'Cuotas, abonos y saldo del crédito' },
  ];
  const ordenPersonal = id => {
    const i = prefs.orden.indexOf(id);
    return i === -1 ? null : i;
  };
  const todasLasCards = isAdmin
    ? panelItems.filter(item => item.visible !== false).sort((a, b) => {
        const pa = ordenPersonal(a.id), pb = ordenPersonal(b.id);
        if (pa !== null && pb !== null) return pa - pb;
        if (pa !== null) return -1;
        if (pb !== null) return 1;
        return Number(a.order || 0) - Number(b.order || 0);
      }).map(item => ({ key: item.id, ...item }))
    : saraCards;
  const cards = todasLasCards.filter(c => !prefs.ocultos.includes(c.key));
  const favoritas = cards.filter(c => prefs.favoritos.includes(c.key));
  const favoritasIds = new Set(favoritas.map(c => c.key));
  // Las favoritas suben a su propia franja, asi que salen de la grilla normal
  // para no aparecer dos veces.
  const cardsGrilla = cards.filter(card => !favoritasIds.has(card.key));
  const gruposVisibles = isAdmin
    ? ADMIN_LANDING_GROUPS.map(group => ({
        ...group,
        items: group.ids.map(id => cards.find(card => card.key === id)).filter(Boolean),
      })).filter(group => group.items.length)
    : [];
  const grupoSeleccionado = gruposVisibles.find(group => group.key === grupoActivo);

  const renderTarjeta = ({ key, icon, label, desc }, extra = {}) => (
    <button className="admin-landing-card" key={key} onClick={() => { setGrupoActivo(null); onGo(key); }} style={{
      width: isAdmin ? 220 : 200, minHeight: isAdmin ? 168 : 'auto', padding: isAdmin ? '24px 18px' : '28px 20px', background: 'rgba(0,184,227,0.06)',
      border: '1px solid rgba(0,184,227,0.2)', borderRadius: 8, cursor: 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      transition: 'all 0.2s', fontFamily: "'Space Grotesk', sans-serif",
      ...extra,
    }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,184,227,0.13)'; e.currentTarget.style.borderColor = 'rgba(0,184,227,0.5)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = extra.background || 'rgba(0,184,227,0.06)'; e.currentTarget.style.borderColor = extra.borderColor || 'rgba(0,184,227,0.2)'; }}
    >
      <span style={{ width: 44, height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: 'rgba(0,184,227,0.12)', color: '#00b8e3', fontSize: 15, fontWeight: 900, letterSpacing: '0.08em' }}>{icon}</span>
      <span className="admin-landing-card-title" style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{label}</span>
      <span className="admin-landing-card-desc" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', textAlign: 'center', lineHeight: 1.4 }}>{desc}</span>
    </button>
  );
  return (
    <div className="admin-landing-shell" style={{ minHeight: '100vh', background: '#08293f', display: 'flex', flexDirection: 'column' }}>
      {/* Topbar */}
      <div className="admin-landing-topbar" style={{ borderBottom: '1px solid rgba(0,184,227,0.2)', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#00b8e3', letterSpacing: -2 }}>{'// '}</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#00b8e3', letterSpacing: 3 }}>AGE</span>
          <span style={{ fontSize: 9, fontWeight: 300, color: theme === 'light' ? '#5a7a8a' : '#b3b3b3', letterSpacing: 3, marginLeft: 2 }}>INFORMÁTICA</span>
        </div>
        <div className="admin-landing-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ThemeToggle theme={theme} onToggle={onToggleTheme} variant={theme === 'light' ? 'default' : 'nav'} />
          <button onClick={() => setPersonalizar(true)} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: '#00b8e3', background: 'rgba(0,184,227,0.1)', border: '1px solid rgba(0,184,227,0.3)', padding: '4px 11px', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" }}>&#9733;<span className="admin-landing-pers-txt"> PERSONALIZAR</span></button>
          <span className="admin-landing-user" style={{ fontSize: 12, color: theme === 'light' ? '#5a7a8a' : 'rgba(255,255,255,0.45)' }}>{user.nombre}</span>
          <button onClick={onLogout} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: theme === 'light' ? '#5a7a8a' : 'rgba(255,255,255,0.5)', background: 'transparent', border: `1px solid ${theme === 'light' ? '#cfdde5' : 'rgba(255,255,255,0.15)'}`, padding: '4px 10px', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" }}>SALIR</button>
        </div>
      </div>

      {/* Content */}
      <div className="admin-landing-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ marginBottom: 8, fontSize: 11, fontWeight: 500, color: 'rgba(0,184,227,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Panel Administrativo</div>
        <div className="admin-landing-heading" style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Bienvenido/a, {user.nombre}</div>
        <div className="admin-landing-muted" style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 40 }}>{mesActual} {hoy.getFullYear()}</div>

        {favoritas.length > 0 && (
          <div style={{ width: '100%', maxWidth: isAdmin ? 1120 : 700, marginBottom: 26 }}>
            <div className="admin-landing-section-title" style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10, textAlign: 'center' }}>Accesos favoritos</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              {favoritas.map(f => (
                <button className="admin-landing-card" key={f.key} onClick={() => onGo(f.key)} style={{
                  minWidth: 250, padding: '15px 18px', background: 'rgba(0,184,227,0.12)',
                  border: '1px solid rgba(0,184,227,0.4)', borderRadius: 9, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left',
                  transition: 'all 0.2s', fontFamily: "'Space Grotesk', sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,184,227,0.2)'; e.currentTarget.style.borderColor = 'rgba(0,184,227,0.65)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,184,227,0.12)'; e.currentTarget.style.borderColor = 'rgba(0,184,227,0.4)'; }}
                >
                  <span style={{ width: 42, height: 42, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 7, background: 'rgba(0,184,227,0.22)', color: '#00b8e3', fontSize: 14, fontWeight: 900, letterSpacing: '0.08em' }}>{f.icon}</span>
                  <span>
                    <span className="admin-landing-card-title" style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{f.label}</span>
                    <span className="admin-landing-card-desc" style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.35 }}>{f.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="admin-landing-groups" style={{ display: 'grid', gridTemplateColumns: isAdmin ? 'repeat(5, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: 12, width: '100%', maxWidth: isAdmin ? 1120 : 700 }}>
          {isAdmin
            ? gruposVisibles.map(group => {
                const resumen = group.items.slice(0, 3).map(item => item.label).join(' · ') + (group.items.length > 3 ? ` · +${group.items.length - 3}` : '');
                return (
                  <button className="admin-landing-card admin-landing-group-card" key={group.key} onClick={() => setGrupoActivo(group.key)} style={{
                    minHeight: 168, padding: '24px 14px', background: 'rgba(0,184,227,0.06)',
                    border: '1px solid rgba(0,184,227,0.2)', borderRadius: 8, cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11,
                    transition: 'all 0.2s', fontFamily: "'Space Grotesk', sans-serif",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,184,227,0.13)'; e.currentTarget.style.borderColor = 'rgba(0,184,227,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,184,227,0.06)'; e.currentTarget.style.borderColor = 'rgba(0,184,227,0.2)'; }}
                  >
                    <span style={{ width: 48, height: 48, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: 'rgba(0,184,227,0.12)', color: '#00b8e3', fontSize: 15, fontWeight: 900, letterSpacing: '0.08em' }}>{group.code}</span>
                    <span className="admin-landing-card-title" style={{ fontSize: 14, fontWeight: 700, color: '#fff', textAlign: 'center' }}>{group.label}</span>
                    <span className="admin-landing-card-desc" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', textAlign: 'center', lineHeight: 1.45 }}>{group.items.length} {group.items.length === 1 ? 'opción' : 'opciones'}</span>
                    <span className="admin-landing-card-desc" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.4 }}>{resumen}</span>
                  </button>
                );
              })
            : cardsGrilla.map(item => renderTarjeta(item, { width: '100%' }))}
        </div>
      </div>

      {grupoSeleccionado && (
        <div
          className="admin-group-overlay"
          onMouseDown={() => setGrupoActivo(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 180, padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(1,18,30,0.72)', backdropFilter: 'blur(3px)' }}
        >
          <section
            className="admin-group-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-group-title"
            onMouseDown={event => event.stopPropagation()}
            style={{ width: '100%', maxWidth: 900, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', padding: 24, background: theme === 'light' ? '#fff' : '#0a3149', border: '1px solid rgba(0,184,227,0.38)', borderRadius: 12, boxShadow: '0 24px 70px rgba(0,0,0,0.42)' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 44, height: 44, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 7, background: 'rgba(0,184,227,0.14)', color: '#00b8e3', fontSize: 13, fontWeight: 900, letterSpacing: '0.08em' }}>{grupoSeleccionado.code}</span>
                <div>
                  <div style={{ color: '#00b8e3', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Módulos</div>
                  <h2 id="admin-group-title" className="admin-landing-heading" style={{ margin: '3px 0 0', color: '#fff', fontSize: 20 }}>{grupoSeleccionado.label}</h2>
                </div>
              </div>
              <button type="button" onClick={() => setGrupoActivo(null)} aria-label="Cerrar" style={{ border: '1px solid rgba(0,184,227,0.28)', background: 'rgba(0,184,227,0.08)', color: '#00b8e3', borderRadius: 7, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800 }}>X</button>
            </div>
            <div className="admin-group-options" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
              {grupoSeleccionado.items.map(item => renderTarjeta(item, { width: '100%', minHeight: 158 }))}
            </div>
          </section>
        </div>
      )}

      {personalizar && (
        <div onClick={() => setPersonalizar(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 340, maxWidth: '100%', height: '100vh', overflowY: 'auto', background: '#0a2f47', borderLeft: '1px solid rgba(0,184,227,0.25)', fontFamily: "'Space Grotesk', sans-serif" }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#00b8e3', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Personalizar panel</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3, lineHeight: 1.4 }}>Tus favoritos y fichas ocultas. Solo afecta tu usuario.</div>
              </div>
              <button onClick={() => setPersonalizar(false)} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', fontWeight: 800, fontSize: 13, fontFamily: 'inherit' }}>X</button>
            </div>

            <div style={{ padding: '10px 0' }}>
              {todasLasCards.map((item, idx) => {
                const esFav = prefs.favoritos.includes(item.key);
                const oculto = prefs.ocultos.includes(item.key);
                return (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', opacity: oculto ? 0.45 : 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <button
                        onClick={() => {
                          const ids = todasLasCards.map(c => c.key);
                          if (idx === 0) return;
                          const o = [...ids];
                          [o[idx - 1], o[idx]] = [o[idx], o[idx - 1]];
                          guardarPrefs({ ...prefs, orden: o });
                        }}
                        disabled={idx === 0}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: idx === 0 ? 'default' : 'pointer', fontSize: 8, lineHeight: 1, padding: '1px 3px', fontFamily: 'inherit' }}
                      >&#9650;</button>
                      <button
                        onClick={() => {
                          const ids = todasLasCards.map(c => c.key);
                          if (idx >= ids.length - 1) return;
                          const o = [...ids];
                          [o[idx], o[idx + 1]] = [o[idx + 1], o[idx]];
                          guardarPrefs({ ...prefs, orden: o });
                        }}
                        disabled={idx >= todasLasCards.length - 1}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: idx >= todasLasCards.length - 1 ? 'default' : 'pointer', fontSize: 8, lineHeight: 1, padding: '1px 3px', fontFamily: 'inherit' }}
                      >&#9660;</button>
                    </div>
                    <span style={{ width: 28, height: 28, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: oculto ? 'rgba(255,255,255,0.06)' : 'rgba(0,184,227,0.14)', color: oculto ? 'rgba(255,255,255,0.3)' : '#00b8e3', fontSize: 10, fontWeight: 900 }}>{item.icon}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: oculto ? 'rgba(255,255,255,0.35)' : '#fff' }}>{item.label}</span>
                    <button
                      onClick={() => !oculto && toggleFavorito(item.key)}
                      title={esFav ? 'Quitar de favoritos' : 'Marcar como favorito'}
                      style={{ background: 'none', border: 'none', cursor: oculto ? 'default' : 'pointer', fontSize: 15, color: esFav ? '#fbbf24' : 'rgba(255,255,255,0.2)', padding: '0 2px', lineHeight: 1, fontFamily: 'inherit' }}
                    >&#9733;</button>
                    <button
                      onClick={() => toggleOculto(item.key)}
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 10, fontFamily: 'inherit' }}
                    >{oculto ? 'Mostrar' : 'Ocultar'}</button>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                onClick={() => guardarPrefs({ favoritos: [], ocultos: [], orden: [] })}
                style={{ width: '100%', padding: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.45)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11 }}
              >Restablecer por defecto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SEG SOCIAL VIEW ────────────────────────────────────────────────────────
export function SegSocialView({ onBack, embedded = false }) {
  const hoy = new Date();
  const defMesIdx = hoy.getMonth() === 0 ? 11 : hoy.getMonth() - 1;
  const defAnio   = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();

  const [mesIdx, setMesIdx]     = useState(defMesIdx);
  const [anio, setAnio]         = useState(defAnio);
  const [persona, setPersona]   = useState('alberto');
  const [empleados, setEmpleados] = useState([]);
  const [pagos, setPagos]       = useState([]);
  const [resumen, setResumen]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');

  const mes = MESES[mesIdx];

  const fetchBase = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get('/empleados'),
        axios.get('/pagos-ss'),
      ]);
      setEmpleados(Array.isArray(r1.data) ? r1.data.filter(e => e.activo) : []);
      setPagos(Array.isArray(r2.data) ? r2.data : []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBase(); }, [fetchBase]);

  // Fetch métricas del año cuando cambia anio o se selecciona Andres
  useEffect(() => {
    if (persona !== 'andres') return;
    axios.get(`/metricas?anio=${anio}`).then(r => setResumen(r.data)).catch(() => setResumen(null));
  }, [persona, anio]);

  const pagoActual = pagos.find(p => p.mes === mes && parseInt(p.anio) === anio && (p.persona || 'alberto') === persona);

  const anios = [];
  for (let y = 2024; y <= hoy.getFullYear() + 1; y++) anios.push(y);

  // ── Cálculo Andres (independiente) ──
  const porMes = resumen?.porMes || [];
  const mesData = porMes.find(m => parseInt(m.num_mes) === mesIdx + 1) || {};
  const noFacturoMes  = parseFloat(mesData.no_facturo_total) || 0;
  const sinFacturaMes = parseFloat(mesData.sin_factura_total) || 0;
  const facturadoSSMes = parseFloat(mesData.facturado_ss_total) || 0;
  const ingresoTotal  = parseFloat(mesData.valor || mesData.ingresos || 0);
  const ingresoBruto  = ingresoTotal + noFacturoMes;
  const ingresoBaseSS = facturadoSSMes || (ingresoTotal - sinFacturaMes);
  const costos    = ingresoBaseSS * 0.632;
  const baseNeta  = ingresoBaseSS * 0.368;
  const ibcAndres = baseNeta * 0.40;
  const saludA    = Math.round(ibcAndres * 0.125);
  const pensionA  = Math.round(ibcAndres * 0.16);
  const arlA      = Math.round(ibcAndres * 0.00522);
  const totalAndres = saludA + pensionA + arlA;

  async function marcarPagado() {
    setSaving(true); setMsg('');
    try {
      await axios.post('/pagos-ss', { mes, anio, fecha_pago: new Date().toISOString().slice(0,10), persona });
      await fetchBase();
      setMsg('✓ Marcado como pagado');
    } catch { setMsg('Error al guardar'); }
    setSaving(false);
  }

  async function desmarcarPagado() {
    if (!pagoActual) return;
    setSaving(true);
    try {
      await axios.delete(`/pagos-ss/${pagoActual.id}`);
      await fetchBase();
      setMsg('Desmarcado');
    } catch { setMsg('Error'); }
    setSaving(false);
  }

  function printAndres() {
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
  @page { size: 5.5in 8.5in; margin: 0.5in; }
  body { font-family: Arial, sans-serif; font-size: 10px; color: #222; }
  h2 { font-size: 13px; margin: 0 0 4px; }
  .sub { font-size: 10px; color: #555; margin-bottom: 14px; }
  .row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #eee; }
  .sec { font-size: 9px; font-weight: 700; color: #08293f; margin: 10px 0 4px; text-transform: uppercase; letter-spacing: 0.06em; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 9px; font-weight: bold; }
  .pag { background: #d1fae5; color: #065f46; } .pen { background: #fef3c7; color: #92400e; }
  .tot { background: #08293f; color: #fff; display: flex; justify-content: space-between; padding: 6px 8px; border-radius: 2px; margin-top: 8px; font-weight: bold; }
</style></head><body>
<h2>Liquidación SS — Independiente</h2>
<div class="sub">${mes} ${anio} &nbsp;|&nbsp; Andrés Giraldo Estrada &nbsp;|&nbsp;
<span class="badge ${pagoActual ? 'pag':'pen'}">${pagoActual ? 'PAGADO' : 'PENDIENTE'}</span></div>
<div class="sec">Base de cotización</div>
${noFacturoMes > 0 ? `
<div class="row"><span>Ingreso total registrado ${mes} ${anio}</span><span><b>$${ingresoBruto.toLocaleString('es-CO')}</b></span></div>
<div class="row"><span>— No facturado por mí</span><span style="color:#dc2626">− $${noFacturoMes.toLocaleString('es-CO')}</span></div>
<div class="row"><span>Ingreso base SS</span><span><b>$${ingresoBaseSS.toLocaleString('es-CO')}</b></span></div>
` : `<div class="row"><span>Ingreso bruto ${mes} ${anio}</span><span><b>$${ingresoBruto.toLocaleString('es-CO')}</b></span></div>`}
<div class="row"><span>Costos asociados (63.2%)</span><span style="color:#dc2626">− $${Math.round(costos).toLocaleString('es-CO')}</span></div>
<div class="row"><span>Base neta (36.8%)</span><span><b>$${Math.round(baseNeta).toLocaleString('es-CO')}</b></span></div>
<div class="row"><span>IBC (40% base neta)</span><span style="color:#6366f1;font-weight:800">$${Math.round(ibcAndres).toLocaleString('es-CO')}</span></div>
<div class="sec">Aportes</div>
<div class="row"><span>Salud (12.5% IBC)</span><span>$${saludA.toLocaleString('es-CO')}</span></div>
<div class="row"><span>Pensión (16% IBC)</span><span>$${pensionA.toLocaleString('es-CO')}</span></div>
<div class="row"><span>ARL (0.522% IBC)</span><span>$${arlA.toLocaleString('es-CO')}</span></div>
<div class="tot"><span>TOTAL APORTES</span><span>$${totalAndres.toLocaleString('es-CO')}</span></div>
<div style="margin-top:14px;font-size:9px;color:#aaa">Documento informativo — ${new Date().toLocaleDateString('es-CO')}</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=560,height=700');
    w.document.write(html); w.document.close(); w.onload = () => w.print();
  }

  function printAlberto() {
    const emp = empleados[0];
    if (!emp) return;
    const c = calcNomina(parseFloat(emp.salario_base), parseFloat(emp.aux_transporte));
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
  @page { size: 5.5in 8.5in; margin: 0.5in; }
  body { font-family: Arial, sans-serif; font-size: 10px; color: #222; }
  h2 { font-size: 13px; margin: 0 0 4px; }
  .sub { font-size: 10px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #08293f; color: #fff; padding: 5px 4px; font-size: 9px; text-align: left; }
  td { padding: 4px; border-bottom: 1px solid #eee; font-size: 9px; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 9px; font-weight: bold; }
  .pag { background: #d1fae5; color: #065f46; } .pen { background: #fef3c7; color: #92400e; }
  .total-row td { background: #08293f; color: #fff; font-weight: bold; }
</style></head><body>
<h2>Liquidación SS — ${emp.nombre}</h2>
<div class="sub">${mes} ${anio} &nbsp;|&nbsp; CC ${emp.cedula} &nbsp;|&nbsp; Salario $${parseFloat(emp.salario_base).toLocaleString('es-CO')} &nbsp;|&nbsp; IBC $${c.ibc.toLocaleString('es-CO')}
&nbsp;|&nbsp; <span class="badge ${pagoActual ? 'pag':'pen'}">${pagoActual ? 'PAGADO' : 'PENDIENTE'}</span></div>
<table>
  <thead><tr><th>Concepto</th><th>%</th><th>Valor</th><th>Pagado por</th></tr></thead>
  <tbody>
    <tr><td>Salud empleado</td><td>4%</td><td>$${c.salud_emp.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>Salud empleador</td><td>8.5%</td><td>$${c.salud_er.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>Pensión empleado</td><td>4%</td><td>$${c.pension_emp.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>Pensión empleador</td><td>12%</td><td>$${c.pension_er.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>ARL</td><td>0.52%</td><td>$${c.arl.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>Caja Compensación</td><td>4%</td><td>$${c.caja.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>SENA</td><td>2%</td><td>$${c.sena.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr><td>ICBF</td><td>3%</td><td>$${c.icbf.toLocaleString('es-CO')}</td><td>Empleador</td></tr>
    <tr class="total-row"><td colspan="2">TOTAL PILA</td><td>$${c.total_pila.toLocaleString('es-CO')}</td><td></td></tr>
  </tbody>
</table>
<div style="margin-top:14px;font-size:9px;color:#aaa">Documento informativo — ${new Date().toLocaleDateString('es-CO')}</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=560,height=700');
    w.document.write(html); w.document.close(); w.onload = () => w.print();
  }

  const selStyle = { background: 'var(--app-input)', color: 'var(--app-heading)', border: '1px solid rgba(0,184,227,0.3)', padding: '6px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer' };
  const labelStyle = { fontSize: 10, color: 'var(--app-muted)', marginBottom: 4, letterSpacing: '0.08em' };
  const card = { background: 'var(--app-surface)', border: '1px solid var(--app-border)', borderRadius: 6, padding: '18px 20px', marginBottom: 20 };

  return (
    <div style={{ minHeight: embedded ? 'auto' : '100vh', background: 'var(--app-bg)', color: 'var(--app-heading)', fontFamily: "'Space Grotesk', sans-serif", borderRadius: embedded ? 6 : 0 }}>
      {!embedded && <div style={{ borderBottom: '1px solid rgba(0,184,227,0.2)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'rgba(0,184,227,0.08)', border: '1px solid rgba(0,184,227,0.25)', color: '#00b8e3', cursor: 'pointer', fontSize: 12, fontWeight: 800, padding: '6px 10px', fontFamily: "'Space Grotesk', sans-serif" }}>← Panel principal</button>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--app-heading)' }}>Seguridad Social</span>
        <span style={{ fontSize: 11, color: 'var(--app-muted)', marginLeft: 4 }}>Liquidación PILA</span>
      </div>}

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(0,184,227,0.75)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Seguridad social</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--app-heading)', marginBottom: 4 }}>Liquidación y pagos PILA</div>
          <div style={{ fontSize: 12, color: 'var(--app-muted)' }}>{mes} {anio}</div>
        </div>
        {/* Selectores */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={labelStyle}>MES</div>
            <select value={mesIdx} onChange={e => setMesIdx(+e.target.value)} style={selStyle}>
              {MESES.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>AÑO</div>
            <select value={anio} onChange={e => setAnio(+e.target.value)} style={selStyle}>
              {anios.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>PERSONA</div>
            <select value={persona} onChange={e => { setPersona(e.target.value); setMsg(''); }} style={{ ...selStyle, borderColor: persona === 'andres' ? 'rgba(99,102,241,0.6)' : 'rgba(0,184,227,0.3)', color: persona === 'andres' ? '#6366f1' : 'var(--app-heading)' }}>
              <option value="alberto">Alberto Garcia Garcia</option>
              <option value="andres">Andrés Giraldo Estrada (Independiente)</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <button onClick={persona === 'andres' ? printAndres : printAlberto} style={{ padding: '8px 16px', background: 'rgba(0,184,227,0.12)', border: '1px solid rgba(0,184,227,0.3)', color: '#00b8e3', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: "'Space Grotesk', sans-serif" }}>
              🖨 Imprimir
            </button>
            {pagoActual
              ? <button onClick={desmarcarPagado} disabled={saving} style={{ padding: '8px 16px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', color: '#10b981', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: "'Space Grotesk', sans-serif" }}>
                  ✓ Pagado — Desmarcar
                </button>
              : <button onClick={marcarPagado} disabled={saving} style={{ padding: '8px 16px', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: "'Space Grotesk', sans-serif" }}>
                  ⏳ Marcar como pagado
                </button>
            }
          </div>
        </div>

        {msg && <div style={{ marginBottom: 16, fontSize: 12, color: '#10b981' }}>{msg}</div>}

        {loading ? (
          <div style={{ color: 'var(--app-muted)', textAlign: 'center', padding: 40 }}>Cargando…</div>
        ) : persona === 'andres' ? (
          /* ── ANDRES — INDEPENDIENTE ── */
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--app-heading)' }}>Andrés Giraldo Estrada — Independiente</div>
                <div style={{ fontSize: 11, color: 'var(--app-muted)', marginTop: 2 }}>Cotización sobre ingresos · {mes} {anio}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--app-muted)' }}>TOTAL APORTES</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{pesos(totalAndres)}</div>
              </div>
            </div>

            {/* Base de cotización */}
            <div style={{ background: 'var(--app-surface-soft)', borderRadius: 4, padding: '12px 14px', marginBottom: 14, fontSize: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--app-muted)', letterSpacing: '0.08em', marginBottom: 10 }}>BASE DE COTIZACIÓN</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 7 }}>
                <span style={{ color: 'var(--app-muted)' }}>Ingreso total registrado — {mes} {anio}</span>
                <span style={{ fontWeight: 700, color: 'var(--app-heading)', textAlign: 'right' }}>{ingresoBruto > 0 ? pesos(ingresoBruto) : <span style={{ color: 'var(--app-muted)' }}>sin datos</span>}</span>
                {facturadoSSMes > 0 && Math.round(facturadoSSMes) !== Math.round(ingresoTotal - sinFacturaMes) && <>
                  <span style={{ color: '#6366f1' }}>Base facturada SS</span>
                  <span style={{ color: '#6366f1', fontWeight: 700, textAlign: 'right' }}>{pesos(facturadoSSMes)}</span>
                </>}
                {noFacturoMes > 0 && <>
                  <span style={{ color: '#f87171' }}>— No facturado por mí</span>
                  <span style={{ color: '#f87171', fontWeight: 600, textAlign: 'right' }}>− {pesos(noFacturoMes)}</span>
                </>}
                {sinFacturaMes > 0 && <>
                  <span style={{ color: '#f87171' }}>— Sin factura</span>
                  <span style={{ color: '#f87171', fontWeight: 600, textAlign: 'right' }}>− {pesos(sinFacturaMes)}</span>
                </>}
                {(noFacturoMes > 0 || sinFacturaMes > 0) && <>
                  <div style={{ gridColumn: '1/-1', height: 1, background: 'var(--app-border)', margin: '2px 0' }} />
                  <span style={{ color: 'var(--app-muted)', fontWeight: 600 }}>Ingreso base SS</span>
                  <span style={{ fontWeight: 700, color: 'var(--app-heading)', textAlign: 'right' }}>{pesos(ingresoBaseSS)}</span>
                </>}
                <span style={{ color: 'var(--app-muted)' }}>— Costos asociados <span style={{ color: '#f87171', fontWeight: 600 }}>63.2%</span></span>
                <span style={{ color: '#f87171', fontWeight: 600, textAlign: 'right' }}>− {pesos(costos)}</span>
                <div style={{ gridColumn: '1/-1', height: 1, background: 'var(--app-border)', margin: '2px 0' }} />
                <span style={{ color: 'var(--app-muted)' }}>Base neta <span style={{ color: 'var(--app-muted)' }}>(36.8%)</span></span>
                <span style={{ fontWeight: 700, color: 'var(--app-heading)', textAlign: 'right' }}>{pesos(baseNeta)}</span>
                <span style={{ color: 'var(--app-muted)' }}>× 40% → <strong style={{ color: '#6366f1' }}>IBC</strong></span>
                <span style={{ fontWeight: 800, color: '#6366f1', fontSize: 15, textAlign: 'right' }}>{pesos(ibcAndres)}</span>
              </div>
            </div>

            {/* Aportes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
              {[
                { label: 'Salud',         pct: '12.5%', val: saludA,      color: '#00b8e3' },
                { label: 'Pensión',       pct: '16%',   val: pensionA,    color: '#6366f1' },
                { label: 'ARL',           pct: '0.52%', val: arlA,        color: '#f59e0b' },
                { label: 'Total aportes', pct: '',      val: totalAndres, color: '#10b981' },
              ].map(({ label, pct, val, color }) => (
                <div key={label} style={{ background: 'var(--app-surface-soft)', borderRadius: 4, padding: '10px 12px', borderTop: `3px solid ${color}` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--app-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    {label}{pct && <span style={{ color, marginLeft: 4 }}>{pct}</span>}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color }}>{pesos(val)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ── ALBERTO — EMPLEADO ── */
          empleados.map(emp => {
            const c = calcNomina(parseFloat(emp.salario_base), parseFloat(emp.aux_transporte));
            return (
              <div key={emp.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--app-heading)' }}>{emp.nombre}</div>
                    <div style={{ fontSize: 11, color: 'var(--app-muted)' }}>CC {emp.cedula} &nbsp;·&nbsp; Salario {pesos(emp.salario_base)} &nbsp;·&nbsp; IBC {pesos(c.ibc)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--app-muted)' }}>TOTAL PILA</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#00b8e3' }}>{pesos(c.total_pila)}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
                  {[
                    { label: 'Salud (12.5%)',  val: c.salud_total,   sub: `${pesos(c.salud_emp)} emp + ${pesos(c.salud_er)} er` },
                    { label: 'Pensión (16%)',   val: c.pension_total, sub: `${pesos(c.pension_emp)} emp + ${pesos(c.pension_er)} er` },
                    { label: 'ARL (0.52%)',     val: c.arl,           sub: 'Empleador' },
                    { label: 'Caja (4%)',       val: c.caja,          sub: 'Empleador' },
                    { label: 'SENA (2%)',       val: c.sena,          sub: 'Empleador' },
                    { label: 'ICBF (3%)',       val: c.icbf,          sub: 'Empleador' },
                  ].map(({ label, val, sub }) => (
                    <div key={label} style={{ background: 'var(--app-surface-soft)', borderRadius: 4, padding: '10px 12px' }}>
                      <div style={{ fontSize: 11, color: 'var(--app-muted)', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--app-heading)' }}>{pesos(val)}</div>
                      <div style={{ fontSize: 10, color: 'var(--app-muted)', marginTop: 2 }}>{sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}

        {/* Historial */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--app-muted)', marginBottom: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Historial de Pagos SS — {persona === 'andres' ? 'Andrés Giraldo Estrada' : 'Alberto Garcia'}
          </div>
          {pagos.filter(p => (p.persona || 'alberto') === persona).length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--app-muted)', textAlign: 'center', padding: 20 }}>Sin registros</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--app-border)' }}>
                  {['Mes', 'Año', 'Fecha pago', 'Pagado por', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--app-muted)', fontWeight: 500, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagos.filter(p => (p.persona || 'alberto') === persona).map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '7px 8px', color: 'var(--app-heading)' }}>{p.mes}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--app-text)' }}>{p.anio}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--app-text)' }}>{p.fecha_pago ? new Date(p.fecha_pago + 'T12:00').toLocaleDateString('es-CO') : '—'}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--app-muted)', fontSize: 11 }}>{p.pagado_por || '—'}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                      <button onClick={async () => { await axios.delete(`/pagos-ss/${p.id}`); fetchBase(); }} style={{ background: 'none', border: 'none', color: 'rgba(255,100,100,0.5)', cursor: 'pointer', fontSize: 12 }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function AdministrativaPage({ user, onLogout, onIngresarNormal, theme, onToggleTheme }) {
  const [vista, setVista] = useState('landing');

  if (vista === 'ss')     return <SegSocialView onBack={() => setVista('landing')} />;
  if (vista === 'nomina') return (
    <div style={{ minHeight: '100vh', background: '#f4f7fa' }}>
      <div style={{ background: '#08293f', borderBottom: '1px solid rgba(0,184,227,0.2)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => setVista('landing')} style={{ background: 'rgba(0,184,227,0.08)', border: '1px solid rgba(0,184,227,0.25)', color: '#00b8e3', cursor: 'pointer', fontSize: 12, fontWeight: 800, padding: '6px 10px', fontFamily: "'Space Grotesk', sans-serif" }}>← Panel principal</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Nómina</span>
      </div>
      <main style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px' }}>
        <NominaPage />
      </main>
    </div>
  );
  if (vista === 'credito') return (
    <div style={{ minHeight: '100vh', background: '#f4f7fa' }}>
      <div style={{ background: '#08293f', borderBottom: '1px solid rgba(0,184,227,0.2)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => setVista('landing')} style={{ background: 'rgba(0,184,227,0.08)', border: '1px solid rgba(0,184,227,0.25)', color: '#00b8e3', cursor: 'pointer', fontSize: 12, fontWeight: 800, padding: '6px 10px', fontFamily: "'Space Grotesk', sans-serif" }}>← Panel principal</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Crédito vehículo</span>
      </div>
      <main style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px' }}>
        <CreditoAutomovil />
      </main>
    </div>
  );

  return (
    <Landing
      user={user}
      onLogout={onLogout}
      theme={theme}
      onToggleTheme={onToggleTheme}
      onGo={key => {
        if (['ss', 'nomina', 'credito'].includes(key)) setVista(key);
        else onIngresarNormal(key === 'normal' ? undefined : key);
      }}
    />
  );
}
